import { useMutation } from "@tanstack/react-query";
import { Plus } from "lucide-react";
import { useState } from "react";
import { useTranslations } from "use-intl";

import { Button } from "@lawline/ui/components/button";
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from "@lawline/ui/components/dialog";

import { useAuth } from "@platform/auth/context";
import { createEndpointMutation } from "../webhooks.mutations";
import type { WebhookEndpointWithSecret } from "../webhooks.types";
import { NEW_ENDPOINT_FIELDS, runKindsPayload } from "../webhooks.utils";
import { WebhookEndpointForm } from "./webhook-endpoint-form";
import { WebhookSecret } from "./webhook-secret";

// On success the secret replaces the form in place, like a new API key.
export function CreateWebhookEndpoint() {
	const t = useTranslations("webhooks.create");
	const { can } = useAuth();
	const create = useMutation(createEndpointMutation);
	const [open, setOpen] = useState(false);
	const [created, setCreated] = useState<WebhookEndpointWithSecret | null>(null);

	// The endpoint exists once the server has the request, and its secret is in the answer alone,
	// so the dialog stays open until the answer arrives.
	function onOpenChange(next: boolean) {
		if (!next && create.isPending) return;
		setOpen(next);
		if (!next) {
			create.reset();
			setCreated(null);
		}
	}

	if (!can({ webhook: ["create"] })) return null;

	return (
		<Dialog open={open} onOpenChange={onOpenChange}>
			<DialogTrigger asChild>
				<Button size="sm">
					<Plus className="size-4" />
					{t("button")}
				</Button>
			</DialogTrigger>
			<DialogContent className="sm:max-w-md">
				{created ? (
					<>
						<DialogHeader>
							<DialogTitle>{t("successTitle")}</DialogTitle>
						</DialogHeader>
						<WebhookSecret secret={created.secret} />
						<DialogFooter>
							<Button onClick={() => onOpenChange(false)}>{t("done")}</Button>
						</DialogFooter>
					</>
				) : (
					<>
						<DialogHeader>
							<DialogTitle>{t("title")}</DialogTitle>
						</DialogHeader>
						<WebhookEndpointForm
							initial={NEW_ENDPOINT_FIELDS}
							pending={create.isPending}
							error={create.error}
							submitLabel={t("submit")}
							pendingLabel={t("submitLoading")}
							onSubmit={(fields) =>
								create.mutate(
									{
										url: fields.url.trim(),
										...(fields.description.trim()
											? { description: fields.description.trim() }
											: {}),
										eventTypes: fields.eventTypes,
										runKinds: runKindsPayload(fields.runKinds),
									},
									{ onSuccess: setCreated },
								)
							}
						/>
					</>
				)}
			</DialogContent>
		</Dialog>
	);
}
